import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
  getDoc,
  setDoc,
  serverTimestamp,
  orderBy,
  WithFieldValue,
} from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { firebaseConfig } from 'config/firebase';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { User } from 'firebase/auth';
import { Meal, UserProfile } from '@llm-macro-tracker/shared';
import { mealConverter, userProfileConverter } from '../lib/firebase/converters';

const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);

export const AnalyticsService = {
  logEvent: (eventName: string, eventParams?: { [key: string]: any }) => {
    logEvent(analytics, eventName, eventParams);
  },

  setUserId: (user: User | null) => {
    if (user) {
      setUserId(analytics, user.uid);
    }
  },

  setUserProperties: (properties: { [key: string]: any }) => {
    setUserProperties(analytics, properties);
  },

  logPageView: (pageName: string, pageLocation: string) => {
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: pageLocation,
    });
  },
};

const appCheckDebugToken = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;

function initializeAppCheckWithDebug() {
  if (appCheckDebugToken) {
    console.log('Setting App Check debug token');
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  } else {
    console.log('App Check debug token not found');
  }

  return initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider('6LeJuTkqAAAAADiKbV0R8NHihyy9jMV0nvMhU34p'),
    isTokenAutoRefreshEnabled: true,
  });
}

initializeAppCheckWithDebug();
console.log('App Check initialized');

const db = getFirestore(app);
const storage = getStorage(app);

export const functions = getFunctions(app);

if (process.env.REACT_APP_USE_EMULATOR === 'true' || process.env.FIREBASE_EMULATOR_HOST) {
  console.log('Using Firebase emulators');

  if (process.env.FIREBASE_FUNCTIONS_EMULATOR_HOST) {
    const [host, port] = process.env.FIREBASE_FUNCTIONS_EMULATOR_HOST.split(':');
    connectFunctionsEmulator(functions, host, parseInt(port, 10));
  } else if (process.env.REACT_APP_USE_EMULATOR === 'true') {
    connectFunctionsEmulator(
      functions,
      process.env.REACT_APP_FUNCTIONS_HOST || 'localhost',
      parseInt(process.env.REACT_APP_FUNCTIONS_PORT || '5001', 10),
    );
  }
}

export const FirebaseService = {
  addMeal: async (
    userId: string,
    meal: Omit<Meal, 'userId' | 'timestamp' | 'v'>,
  ): Promise<string> => {
    const mealWithMetadata = {
      ...meal,
      userId,
      date: Timestamp.fromDate(new Date(meal.date)),
      timestamp: serverTimestamp(),
      imageId: meal.imageId || null,
      v: 2,
    };

    const docRef = await addDoc(
      collection(db, 'meals').withConverter(mealConverter),
      mealWithMetadata as WithFieldValue<Meal>,
    );
    return docRef.id;
  },

  getMealsForDate: async (userId: string, date: Date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, 'meals').withConverter(mealConverter),
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(startOfDay)),
      where('date', '<=', Timestamp.fromDate(endOfDay)),
      orderBy('timestamp', 'asc'),
    );

    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error getting meals: ', error);
      throw error;
    }
  },

  deleteMeal: async (userId: string, mealId: string) => {
    try {
      const mealRef = doc(db, 'meals', mealId).withConverter(mealConverter);
      const mealSnapshot = await getDocs(
        query(collection(db, 'meals').withConverter(mealConverter), where('userId', '==', userId)),
      );

      if (mealSnapshot.empty) {
        throw new Error('Meal not found or user not authorized to delete this meal');
      }

      await deleteDoc(mealRef);
    } catch (error) {
      console.error('Error deleting meal: ', error);
      throw error;
    }
  },

  updateMeal: async (
    userId: string,
    mealId: string,
    updatedMeal: Omit<Meal, 'userId' | 'timestamp' | 'v'>,
  ) => {
    try {
      const mealRef = doc(db, 'meals', mealId).withConverter(mealConverter);
      const mealDoc = await getDoc(mealRef);

      if (!mealDoc.exists()) {
        throw new Error('Meal not found');
      }

      const existingMeal = mealDoc.data();

      const mergedMeal = {
        ...existingMeal,
        ...updatedMeal,
        userId,
        v: 2, // Set to the current version
      };

      await updateDoc(mealRef, mergedMeal);
      return mealId;
    } catch (error) {
      console.error('Error updating meal: ', error);
      throw error;
    }
  },

  getUserProfile: async (userId: string): Promise<UserProfile | null> => {
    try {
      const docRef = doc(db, 'userProfiles', userId).withConverter(userProfileConverter);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  },

  updateUserProfile: async (userId: string, profile: UserProfile): Promise<void> => {
    try {
      const docRef = doc(db, 'userProfiles', userId).withConverter(userProfileConverter);
      const cleanProfile = Object.entries(profile).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);
      await setDoc(docRef, cleanProfile);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  uploadImage: async (userId: string, file: File): Promise<string> => {
    try {
      // Client-side validation
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please upload an image.');
      }

      // Limit file size (e.g., 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size exceeds 5MB limit.');
      }

      // Determine file extension based on mime type
      const fileExtension = file.type.split('/')[1];
      const allowedExtensions = ['jpeg', 'jpg', 'png', 'gif', 'webp'];

      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error('Invalid file type. Allowed types are JPEG, PNG, GIF, and WebP.');
      }

      // Generate a unique filename
      const uniqueFileName = `${uuidv4()}.${fileExtension}`;
      const storageRef = ref(storage, `user-uploads/meal-images/${userId}/${uniqueFileName}`);

      // Add metadata
      const metadata = {
        customMetadata: {
          uploadTimestamp: Date.now().toString(),
          userId: userId,
        },
      };

      // Upload file with metadata
      await uploadBytes(storageRef, file, {
        contentType: file.type,
        ...metadata,
      });

      // Return the storage reference path
      return uniqueFileName;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },
};
